'use client';

import { Button } from '@ahafood/ui/components/button';
import { Image } from '@ahafood/ui/components/image';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { Edit2 } from 'lucide-react';
import React, { useMemo } from 'react';

import {
  useAIPlannerStore,
  EATING_GOALS,
  DIET_TYPES,
  ALLERGIES,
  WEEKDAYS,
  type AgeRange,
} from '@/store/ai-planner';

interface PlanOverviewTabProps {
  onEditPlan: () => void;
}

const AGE_RANGE_LABELS: Record<AgeRange, string> = {
  under_18: 'Dưới 18',
  '18_22': '18 – 22',
  '23_30': '23 – 30',
  '31_40': '31 – 40',
  over_40: 'Trên 40',
};

const GENDER_LABELS: Record<'male' | 'female', string> = {
  male: 'Nam',
  female: 'Nữ',
};

export function PlanOverviewTab({ onEditPlan }: PlanOverviewTabProps) {
  const currentPlan = useAIPlannerStore.use.currentPlan();
  const preferences = useAIPlannerStore.use.preferences();

  const statistics = useMemo(() => {
    if (!currentPlan?.weeks) {
      return {
        totalWeeks: 0,
        totalMeals: 0,
        startDate: '',
        endDate: '',
      };
    }

    const totalMeals = currentPlan.weeks.reduce((total, week) => {
      return (
        total +
        week.days.reduce((dayTotal, day) => {
          return (
            dayTotal + day.meals.filter((m) => m.status !== 'cancelled').length
          );
        }, 0)
      );
    }, 0);

    return {
      totalWeeks: currentPlan.weeks.length,
      totalMeals,
      startDate: currentPlan.weeks[0]?.startDate ?? '',
      endDate: currentPlan.weeks[currentPlan.weeks.length - 1]?.endDate ?? '',
    };
  }, [currentPlan]);

  // Get labels for user preferences
  const userPreferencesDisplay = useMemo(() => {
    const deliveryDaysLabel = preferences.deliveryDays
      ?.map((day) => WEEKDAYS.find((w) => w.value === day)?.shortLabel)
      .filter(Boolean)
      .join(', ');

    const eatingGoalsLabel = preferences.eatingGoals
      ?.map((goal) => EATING_GOALS.find((g) => g.value === goal)?.label)
      .filter(Boolean)
      .join(', ');

    const dietTypeLabel = preferences.dietType
      ? DIET_TYPES.find((d) => d.value === preferences.dietType)?.label
      : undefined;

    const allergiesLabel = preferences.allergies
      ?.map((allergy) => ALLERGIES.find((a) => a.value === allergy)?.label)
      .filter(Boolean)
      .join(', ');

    const ageRangeLabel = preferences.ageRange
      ? AGE_RANGE_LABELS[preferences.ageRange]
      : undefined;

    const genderLabel = preferences.gender
      ? GENDER_LABELS[preferences.gender]
      : undefined;

    const budgetLabel = preferences.budget
      ? new Intl.NumberFormat('vi-VN').format(preferences.budget) + '₫'
      : undefined;

    return {
      deliveryDays: deliveryDaysLabel,
      eatingGoals: eatingGoalsLabel,
      dietType: dietTypeLabel,
      allergies: allergiesLabel,
      ageRange: ageRangeLabel,
      gender: genderLabel,
      budget: budgetLabel,
    };
  }, [preferences]);

  if (!currentPlan) {
    return null;
  }

  return (
    <div className="flex flex-col h-full py-6">
      {/* AI Avatar Section */}
      <div className="flex flex-col items-center mb-6">
        <div className="relative mb-4">
          {/* <div className="size-24 rounded-full overflow-hidden backdrop-blur-xl bg-white/10 border border-white/20 flex items-center justify-center p-3">
            <Image
              src={cn(
                userPreferencesDisplay.gender === 'Nam'
                  ? `${assetPrefix}/static/images/avatar-male.webp`
                  : `${assetPrefix}/static/images/avatar-female.webp`
              )}
              alt="AI Avatar"
              width={120}
              height={120}
              className="object-contain rounded-full"
            />
          </div> */}
          <div className="relative size-20 rounded-full overflow-hidden">
            <Image
              src={cn(
                userPreferencesDisplay.gender === 'Nam'
                  ? `${assetPrefix}/static/images/avatar-male.webp`
                  : `${assetPrefix}/static/images/avatar-female.webp`
              )}
              alt="AI Avatar"
              fill
              className="object-cover"
            />
          </div>
          {/* Glow effect */}
          <div className="absolute inset-0 rounded-full bg-[#ff8200]/20 blur-2xl -z-10" />
        </div>

        <h3 className="text-white font-semibold text-lg mb-1">
          Kế hoạch ăn thông minh
        </h3>
        <p className="text-white/60 text-xs text-center max-w-[280px]">
          Được thiết kế riêng dựa trên sở thích và mục tiêu của bạn
        </p>
      </div>

      {/* Plan Statistics */}
      <div className="mb-4">
        <h4 className="text-white/80 text-sm font-semibold mb-2">
          Thông tin kế hoạch
        </h4>
        <div className="space-y-2">
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-3">
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">Thời gian</span>
              <span className="text-white font-medium text-sm">
                {statistics.totalWeeks} tuần
              </span>
            </div>
          </div>

          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-3">
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">Tổng số bữa</span>
              <span className="text-white font-medium text-sm">
                {statistics.totalMeals} bữa
              </span>
            </div>
          </div>

          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-3">
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">Ngày giao</span>
              <span className="text-white font-medium text-sm">
                {userPreferencesDisplay.deliveryDays ?? '-'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* User Preferences */}
      <div className="mb-6">
        <h4 className="text-white/80 text-sm font-semibold mb-2">
          Thông tin cá nhân
        </h4>
        <div className="space-y-2">
          {userPreferencesDisplay.ageRange && (
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Độ tuổi</span>
                <span className="text-white font-medium text-sm">
                  {userPreferencesDisplay.ageRange}
                </span>
              </div>
            </div>
          )}

          {userPreferencesDisplay.gender && (
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Giới tính</span>
                <span className="text-white font-medium text-sm">
                  {userPreferencesDisplay.gender}
                </span>
              </div>
            </div>
          )}

          {userPreferencesDisplay.budget && (
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Ngân sách/bữa</span>
                <span className="text-white font-medium text-sm">
                  {userPreferencesDisplay.budget}
                </span>
              </div>
            </div>
          )}

          {userPreferencesDisplay.eatingGoals && (
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Mục tiêu</span>
                <span className="text-white font-medium text-sm">
                  {userPreferencesDisplay.eatingGoals}
                </span>
              </div>
            </div>
          )}

          {userPreferencesDisplay.dietType && (
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Chế độ ăn</span>
                <span className="text-white font-medium text-sm">
                  {userPreferencesDisplay.dietType}
                </span>
              </div>
            </div>
          )}

          {userPreferencesDisplay.allergies && (
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Không ăn</span>
                <span className="text-white font-medium text-sm text-right max-w-[180px]">
                  {userPreferencesDisplay.allergies}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Button */}
      <Button
        onClick={onEditPlan}
        variant="outline"
        className="w-full border-white/20 text-white bg-transparent hover:bg-white/10 rounded-full"
      >
        <Edit2 className="size-4 mr-2" />
        Chỉnh sửa kế hoạch
      </Button>
    </div>
  );
}
